import { Async } from '../../platform/async/Async'
import { useToast } from '../../platform/feedback/ToastProvider'
import { Skeleton } from '../../platform/async/Skeleton'
import { Button } from '../../platform/primitives/Button'
import { DataGrid, type Column } from '../../platform/primitives/DataGrid'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { useNames } from './RuleFlow'
import { count } from './applyCopy'
import { changeLines } from './ruleChanges'
import { AmountCell, DateCell, MerchantCell, rowName } from './ruleRowCells'
import type { Names } from './ruleText'
import { useRemoveFromRule, type Removed } from './useRuleApply'
import type { RuleLog } from './useRuleLogs'
import { useRuleLogs } from './useRuleLogs'
import { logRowTxn, txnRow, type RuleRowTxn } from './useRuleRowTxns'

export const APPLIED_EMPTY = "This rule hasn't changed any transactions yet."
export const REMOVE_FAILED = "Couldn't remove it from the rule. Nothing was changed. Try again in a moment."

/** One change of a log the revert has not already undone, as the wire writes it. */
type OpenChange = { field: string; action: string; next: unknown }

function openChanges(l: RuleLog): OpenChange[] {
  return l.changes.flatMap((c) => {
    if (typeof c !== 'object' || c === null) return []
    const r = c as Record<string, unknown>
    if (typeof r.field !== 'string') return []
    // A change undone by an earlier partial revert carries `reverted_at`; this revert skips it.
    if (typeof r.reverted_at === 'string' && r.reverted_at !== '') return []
    return [{ field: r.field, action: typeof r.action === 'string' ? r.action : '', next: r.new_value }]
  })
}

/** A tag change or an appended note line is undone surgically, so a lock never holds it back
 *  (backend `_revert_change`): it can be skipped as changed, never as locked. */
const surgical = (c: OpenChange) => c.field === 'tags' || (c.field === 'user_notes' && c.action === 'append_note')

type Named = 'category_id' | 'merchant_id'

/**
 * The category or merchant the revert left, when it left exactly one field and that field can be
 * told from the transaction as read BEFORE the write. `null` when it cannot be told: then the toast
 * names no value rather than the wrong one (rv-review I3: the log's FIRST field is not evidence).
 *
 * - Changed since: a category or merchant that no longer holds what the rule wrote is the one the
 *   server skips. With one skip, exactly one such field is that skip.
 * - Locked: the response does not say which field, and the transaction does not carry its locks. Only
 *   a log with a single lockable change identifies it.
 */
function leftField(r: Removed, l: RuleLog, t: RuleRowTxn): Named | null {
  if (t.status !== 'found' || r.changed + r.locked !== 1) return null
  const open = openChanges(l)
  const candidates =
    r.locked === 1
      ? open.filter((c) => !surgical(c))
      : open.filter((c) => (c.field === 'category_id' && t.categoryId !== c.next) || (c.field === 'merchant_id' && t.merchantId !== c.next))
  // A category not known here (no read before the write) cannot be ruled in or out.
  if (r.locked === 0 && t.categoryId === undefined && open.some((c) => c.field === 'category_id')) return null
  const only = candidates.length === 1 ? candidates[0] : undefined
  return only?.field === 'category_id' || only?.field === 'merchant_id' ? only.field : null
}

/** "stays Groceries" / "stays uncategorised" / "stays Bean Hut"; "stays as it is" when unnamed. */
function staysAs(field: Named, t: RuleRowTxn, names: Names): string {
  if (t.status !== 'found') return 'stays as it is'
  if (field === 'merchant_id') return `stays ${t.merchant}`
  if (t.categoryId === undefined) return 'stays as it is'
  if (t.categoryId === null) return 'stays uncategorised'
  const name = names.category(t.categoryId)
  return name === undefined ? 'stays as it is' : `stays ${name}`
}

const NOT_AVAILABLE = 'the category or merchant it had before, or the transaction itself, is no longer available to you.'

/**
 * The toast after Remove, from the revert's own counters:
 *
 * - `failed`: the revert was refused (the category or merchant it would put back is gone, or the
 *   transaction is): nothing of that application was put back, and the toast says so.
 * - `skipped_changed` / `skipped_locked`: fields left. One that can be identified (`leftField`) is
 *   named with what it stays as: "Its category has changed since, so it stays Groceries." / "You
 *   set its category yourself, so it stays Groceries." Otherwise no field is named.
 */
export function removedMessage(r: Removed, l: RuleLog, t: RuleRowTxn, names: Names): string {
  const skips = r.changed + r.locked
  if (r.failed > 0) {
    return r.reverted + skips === 0
      ? `Removed from the rule, but its changes couldn't be undone: ${NOT_AVAILABLE}`
      : `Removed from the rule. Some of its changes couldn't be undone: ${NOT_AVAILABLE}`
  }
  if (skips === 0) return "Removed from the rule. It's back the way it was."
  const field = leftField(r, l, t)
  if (field !== null) {
    const label = field === 'category_id' ? 'category' : 'merchant'
    const stays = staysAs(field, t, names)
    return r.locked > 0
      ? `Removed from the rule. You set its ${label} yourself, so it ${stays}.`
      : `Removed from the rule. Its ${label} has changed since, so it ${stays}.`
  }
  if (skips === 1) {
    return r.locked > 0
      ? 'Removed from the rule. You set part of it yourself, so that part stays as it is.'
      : 'Removed from the rule. Part of it has changed since, so that part stays as it is.'
  }
  if (r.locked === 0) return `Removed from the rule. ${count(skips)} of its fields have changed since, so they stay as they are.`
  if (r.changed === 0) return `Removed from the rule. You set ${count(skips)} of its fields yourself, so they stay as they are.`
  return `Removed from the rule. ${count(r.changed)} of its fields changed since and you set ${count(r.locked)} yourself, so they stay as they are.`
}

/** A refusal is a warning; a field left alone, which the user may expect, is information. */
const removedKind = (r: Removed) => (r.failed > 0 ? 'warning' : r.changed + r.locked > 0 ? 'info' : 'success')

/** Every merchant id a log's changes name, so `useNames` can read each by id. */
function merchantIdsOf(logs: readonly RuleLog[]): string[] {
  const ids = new Set<string>()
  for (const l of logs) {
    for (const c of l.changes) {
      if (typeof c !== 'object' || c === null) continue
      const r = c as Record<string, unknown>
      if (r.action !== 'set_merchant') continue
      for (const v of [r.previous_value, r.new_value]) if (typeof v === 'string' && v !== '') ids.add(v)
    }
  }
  return [...ids]
}

function Changes({ lines }: { lines: string[] }) {
  return (
    <ul className="rule-applied__changes">
      {lines.map((l, i) => (
        // A log's changes have no ids; their order is the order the rule's actions ran.
        <li key={`${i}-${l}`} className="rule-applied__change">
          {l}
        </li>
      ))}
    </ul>
  )
}

/**
 * "Applied to": every change this rule made that is still in effect, one row per application. Each
 * row comes from its log's embedded transaction (`logRowTxn`), so the amount is in that
 * transaction's own currency and sign, with no read per row. A log whose transaction is no longer
 * available keeps its row, named from the log's snapshot, with `TXN_GONE`.
 *
 * **Remove** (plan Task 6) excludes the transaction from this rule with `revert: true`: it goes back
 * the way it was, and the rule stays off it for good. It then appears under Removed, with Put back.
 */
export function RuleApplied({ ruleId }: { ruleId: string }) {
  const { state, hasMore, loadingMore, loadMore } = useRuleLogs(ruleId)
  const logs = state.status === 'success' ? state.data.logs : []
  const names = useNames(merchantIdsOf(logs))
  const phone = useBreakpoint() === 'phone'
  const remove = useRemoveFromRule(ruleId)
  const toast = useToast()

  const onRemove = (l: RuleLog) => {
    if (remove.isPending) return
    remove.mutate(l.transactionId, {
      // `before` is the transaction read just ahead of the write; without it, the log's summary,
      // which cannot tell a category apart (`leftField`).
      onSuccess: ({ removed, before }) => {
        const t = before === null ? logRowTxn(l) : txnRow(before)
        toast.show({ kind: removedKind(removed), message: removedMessage(removed, l, t, names) })
      },
      onError: () => toast.show({ kind: 'error', message: REMOVE_FAILED }),
    })
  }

  const removeButton = (l: RuleLog) => {
    const busy = remove.isPending && remove.variables === l.transactionId
    const name = rowName(logRowTxn(l), l.merchantText ?? 'this transaction')
    return (
      <span className="rule-applied__actions">
        <Button
          variant="outline"
          size="panel"
          aria-label={`Remove ${name} from this rule`}
          aria-disabled={remove.isPending || undefined}
          aria-busy={busy || undefined}
          onClick={() => onRemove(l)}
        >
          {busy ? 'Removing…' : 'Remove'}
        </Button>
      </span>
    )
  }

  const columns: Column<RuleLog>[] = [
    { key: 'date', header: 'Date', width: 'minmax(0, 0.7fr)', render: (l) => <DateCell t={logRowTxn(l)} /> },
    {
      key: 'merchant',
      header: 'Merchant',
      width: 'minmax(0, 1.4fr)',
      render: (l) => <MerchantCell id={l.transactionId} t={logRowTxn(l)} snapshot={l.merchantText ?? 'Unknown'} />,
    },
    { key: 'change', header: 'What changed', width: 'minmax(0, 2fr)', render: (l) => <Changes lines={changeLines(l.changes, names)} /> },
    { key: 'amount', header: 'Amount', width: 'minmax(0, 0.9fr)', align: 'end', render: (l) => <AmountCell t={logRowTxn(l)} /> },
    { key: 'action', header: 'Action', width: 'minmax(0, 0.8fr)', align: 'end', render: removeButton },
  ]

  return (
    <Async
      surface="rule-applied"
      state={state}
      skeleton={
        <>
          <Skeleton height={44} />
          <Skeleton height={44} />
          <Skeleton height={44} />
        </>
      }
      empty={{ title: APPLIED_EMPTY }}
    >
      {({ logs: rows }) => (
        <>
          {phone ? (
            <ul className="rule-applied__cards" aria-label="Transactions this rule changed">
              {rows.map((l) => {
                const t = logRowTxn(l)
                return (
                  <li key={l.id} className="rule-applied__card">
                    <div className="rule-applied__card-top">
                      <MerchantCell id={l.transactionId} t={t} snapshot={l.merchantText ?? 'Unknown'} />
                      <AmountCell t={t} />
                    </div>
                    <DateCell t={t} />
                    <Changes lines={changeLines(l.changes, names)} />
                    {removeButton(l)}
                  </li>
                )
              })}
            </ul>
          ) : (
            <DataGrid
              columns={columns}
              groups={[{ label: '', rows }]}
              rowKey={(l) => l.id}
              label="Transactions this rule changed"
              empty={APPLIED_EMPTY}
            />
          )}
          {hasMore && (
            <div className="rule-applied__more">
              <Button
                variant="outline"
                size="panel"
                aria-disabled={loadingMore || undefined}
                onClick={loadingMore ? undefined : loadMore}
              >
                {loadingMore ? 'Loading…' : 'Show more'}
              </Button>
            </div>
          )}
        </>
      )}
    </Async>
  )
}
